import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { SecondaryButton } from 'components/Shared/Button'
import SentinelInput from 'components/Sentinel/SentinelInput'
import Results from 'components/Sentinel/Results'
import Query from 'components/Sentinel/Query'
import Guide from './Guide'
import SavedQuestions from './SavedQuestions/SavedQuestions'
import { actions, useSentinelContext, useSentinelDispatchContext } from './SentinelProvider'
import { Prompt } from './Prompt'
import SideDrawer from 'components/Shared/SideDrawer'
import CustomerDetails from './Results/CustomerDetails'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from 'components/Shared/ErrorFallback'
import axios from 'axios'
import { useOutsideCheckClick } from '../../hooks/UseOnClickOutside'
import LoadingProgressBar from 'components/Shared/LoadingProgressBar'
import DataIntegrationsProvider, {
  useDataIntegrations,
} from 'components/DataIntegrations/DataIntegrationsProvider'
import ExportButton from 'components/DataIntegrations/ExportButton'

function SentinelContent() {
  const { t } = useTranslation()
  const history = useHistory()
  const cancelToken = useRef(null)
  const { exportNLQToCRM, getSupportedCRMs } = useDataIntegrations()

  const createCancelToken = () => {
    cancelToken.current = axios.CancelToken.source()
    return cancelToken.current.token
  }

  const [exportResultsOpen, setExportResultsOpen] = useState(false)
  const [savedQuestionsOpen, setSavedQuestionsOpen] = useState(false)
  const [{ promptType, questionId, questionName }, setPromptType] = useState({
    promptType: null,
    questionId: null,
    questionName: null,
  })
  const desktopContainerRef = useRef()
  const mobileContainerRef = useRef()
  useOutsideCheckClick(
    [desktopContainerRef, mobileContainerRef],
    () => setExportResultsOpen(false),
    exportResultsOpen,
  )

  const [selectedResultId, setSelectedResultId] = useState(null)
  const {
    loading,
    results: { data: resultsData, outcome: resultsOutcome },
    answer,
    error,
  } = useSentinelContext()
  const {
    dispatch,
    fetchSavedQuestions,
    deleteQuestion,
    exportToCSV,
    exportToExcel,
    getIdsToExport,
    askAgain,
    getRunningQuestionStatus,
    cancelRunningQuestion,
    fetchPreparedResult,
    getActiveSavedQuestion,
  } = useSentinelDispatchContext()

  const cancelRequest = () => {
    cancelRunningQuestion()
    if (cancelToken.current) {
      cancelToken.current.cancel('Request canceled by user')
    }
  }

  useEffect(() => {
    const urlParams = new URL(window.location.href)
    const nlqTaskId = urlParams.searchParams.get('task_id')
    if (nlqTaskId) {
      fetchPreparedResult(nlqTaskId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchSavedQuestions()
  }, [fetchSavedQuestions])

  useEffect(() => {
    const urlParams = new URL(window.location.href)
    const drawerOpen = urlParams.searchParams.get('drawer-open')

    if (!drawerOpen && selectedResultId) {
      setSelectedResultId(null)
      return
    }

    const resultId = urlParams.searchParams.get('result-id')
    // eslint-disable-next-line eqeqeq
    if (!resultId || selectedResultId == resultId) return

    setSelectedResultId(resultId)
  }, [dispatch, selectedResultId])

  const handleExport = async (format, secretId = null, containerName = null) => {
    if (format === 'excel') {
      exportToExcel(answer.query_id)
      return { success: true }
    } else if (format === 'csv') {
      exportToCSV(answer.query_id)
      return { success: true }
    } else if (format === 'crm' && secretId) {
      const secret = getSupportedCRMs().find((s) => s.id === secretId)
      if (!secret) {
        throw new Error('Secret not found')
      }
      const selectedIds = getIdsToExport()
      const ids = selectedIds.size > 0 ? Array.from(selectedIds) : answer.data
      const result = await exportNLQToCRM({
        crm_name: secret.service_name,
        secret_id: secret.id,
        container_name: containerName,
        nlq_id: answer.query_id,
        ids,
      })
      return result
    }
  }

  const { loading: taskLoading, isOverdue } = getRunningQuestionStatus()
  const showProgressBar = (loading || taskLoading) && !isOverdue
  const showOverdueLoading = (loading || taskLoading) && isOverdue
  const exportName = useMemo(() => {
    const name = getActiveSavedQuestion()?.name || t('Sentinel.input.defaultQuestionName', {
      date: moment().format('DD.MM.YYYY HH:mm'),
    })
    return `Sentinel_${name}`
  }, [t, getActiveSavedQuestion])

  return (
    <>
      <div className="main-title hide-mobile">
        <div className="d-flex align-items-center gap-2 mr-auto">
          <h1 className="hide-mobile">{t('Sentinel.MainTitle')}</h1>
          <span className="preview-label">({t('common.preview')})</span>
        </div>
        <div
          style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '.5rem' }}
          ref={desktopContainerRef}
        >
          <ExportButton
            buttonText={t('Sentinel.exportResults')}
            buttonClassName="export-results"
            dropdownClassName="sentinel_dropdown"
            disabled={resultsData.length === 0}
            supportedFormats={
              resultsOutcome === 'customers' ? ['csv', 'excel', 'external'] : ['csv', 'excel']
            }
            onExport={handleExport}
            exportName={exportName.replace(/_/g, ' ')}
          />
          <SecondaryButton
            classNames="saved-questions"
            text={t('Sentinel.savedQuestions.title')}
            color="orange"
            fullWidth={false}
            onClick={() => setSavedQuestionsOpen(true)}
          />
        </div>
      </div>

      <div className="sentinel_mobile_topbar hide-desktop">
        <ExportButton
          buttonText={t('Sentinel.exportResults')}
          buttonClassName="export-results"
          dropdownClassName="sentinel_dropdown"
          disabled={resultsData.length === 0}
          supportedFormats={
            resultsOutcome === 'customers' ? ['csv', 'excel', 'external'] : ['csv', 'excel']
          }
          onExport={handleExport}
          exportName={exportName}
        />
        <SecondaryButton
          classNames="saved-questions"
          text={t('Sentinel.savedQuestions.title')}
          color="orange"
          fullWidth={false}
          onClick={() => setSavedQuestionsOpen(true)}
        />
      </div>
      <div className="sentinel_container">
        <div className="sentinel_input">
          <SentinelInput createCancelToken={createCancelToken} onCancelRequest={cancelRequest} />
        </div>
        <div className="sentinel_content">
          {error && (
            <div className="sentinel_error">
              {t(['common.error', 'common.errors.unknownError'])}:&nbsp;{t(error)}
            </div>
          )}
          <Guide />
          <Query />
          {showProgressBar && (
            <LoadingProgressBar visible={showProgressBar} text={t('Sentinel.loading.defaultMsg')} />
          )}
          {showOverdueLoading && (
            <div className="sentinel_loading-overdue">
              {t('Sentinel.loading.overdueMsg')}
              <br />
              {t('Sentinel.loading.overdueDescription')}
              <div className="sentinel_loading-overdue__subtext">
                {t('Sentinel.loading.overdueSubtext')}
              </div>
            </div>
          )}
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Results
              onRowClick={(row) => {
                // we can only show customer details for now
                if (!answer || resultsOutcome !== 'customers') return
                setSelectedResultId(row.id)
                history.replace(`/sentinel/?drawer-open=true&result-id=${row.id}`)
              }}
            />
          </ErrorBoundary>
        </div>
        <SavedQuestions
          isOpen={savedQuestionsOpen && !promptType}
          setOpen={setSavedQuestionsOpen}
          onAskAgain={(id) => {
            dispatch({ type: actions.setError, payload: null })
            if (resultsData.length > 0) setPromptType({ promptType: 'askAgain', questionId: id })
            else {
              setPromptType({})
              setSavedQuestionsOpen(false)
              askAgain(id, createCancelToken())
            }
          }}
          onDelete={(id, name) => {
            dispatch({ type: actions.setError, payload: null })
            setPromptType({ promptType: 'delete', questionId: id, questionName: name })
          }}
        />
      </div>
      <Prompt
        isOpen={!!promptType}
        title={t(`Sentinel.prompt.${promptType}.title`)}
        description={t(`Sentinel.prompt.${promptType}.description`)}
        cancelTxt={t(`Sentinel.prompt.${promptType}.cancel`)}
        proceedTxt={t(`Sentinel.prompt.${promptType}.proceed`)}
        onNo={() => {
          setPromptType({})
          setSavedQuestionsOpen(true)
        }}
        onYes={() => {
          if (promptType === 'askAgain') {
            askAgain(questionId, createCancelToken())
            setSavedQuestionsOpen(false)
            setPromptType({})
          } else if (promptType === 'delete') {
            deleteQuestion(questionId, questionName, ({ success }) => {
              if (success) {
                setPromptType({})
                setSavedQuestionsOpen(true)
              }
            })
          }
        }}
      />
      <SideDrawer toggle={() => {}} classes="no-padding">
        {selectedResultId && <CustomerDetails id={selectedResultId} />}
      </SideDrawer>
    </>
  )
}

export default function Sentinel() {
  return (
    <DataIntegrationsProvider>
      <SentinelContent />
    </DataIntegrationsProvider>
  )
}
